import React, { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import postService, { Post } from '../../services/postService'
import reelService, { Reel } from '../../services/reelService'
import { Bookmark, Heart, MessageCircle, Link2, Check, Video, FileText, Play } from 'lucide-react'
import { formatDistanceToNow } from '../../lib/utils'
import { GovLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'

// Helper to get media URL
const getMediaUrl = (url: string): string => {
  if (!url) return ''
  if (url.startsWith('http')) return url
  // Don't add /api prefix to /uploads/ - nginx serves these directly
  if (url.startsWith('/uploads/')) return url
  return url.startsWith('/') ? `/api${url}` : `/api/${url}`
}

// Post Card Component - Memoized for performance
const SavedPostCard = React.memo(({
  post,
  onUnsave,
  onLike,
  onCopyLink,
  copiedPostId
}: {
  post: Post
  onUnsave: (id: number) => void
  onLike: (id: number) => void
  onCopyLink: (id: number) => void
  copiedPostId: number | null
}) => {
  return (
    <GovCard className="hover:shadow-md transition-shadow">
      {/* Post Header */}
      <div className="flex items-center justify-between mb-3">
        <Link
          to={`/profile/${post.user.username}`}
          className="flex items-center gap-3"
        >
          <img
            src={post.user.profileImage ? getMediaUrl(post.user.profileImage) : '/default-avatar.svg'}
            alt={post.user.firstName}
            className="w-10 h-10 rounded-full object-cover"
            loading="lazy"
          />
          <div>
            <p className="font-medium text-gray-900">
              {post.user.firstName} {post.user.lastName}
            </p>
            <p className="text-sm text-gray-500">
              @{post.user.username}
            </p>
          </div>
        </Link>
        <button
          onClick={() => onUnsave(post.id)}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          title="Unsave"
        >
          <Bookmark className="w-5 h-5 fill-blue-600 text-blue-600" />
        </button>
      </div>

      {/* Post Content */}
      <p className="mb-3 text-gray-700 whitespace-pre-wrap">{post.content}</p>

      {/* Post Media */}
      {post.media && post.media.length > 0 && (
        <div className="mb-3 rounded-lg overflow-hidden">
          {post.media.length === 1 ? (
            <img
              src={getMediaUrl(post.media[0].mediaUrl)}
              alt="Post media"
              className="w-full max-h-[400px] object-cover"
              loading="lazy"
            />
          ) : (
            <div className="grid grid-cols-2 gap-1">
              {post.media.slice(0, 4).map((m, i) => (
                <img
                  key={m.id}
                  src={getMediaUrl(m.mediaUrl)}
                  alt={`Media ${i + 1}`}
                  className="w-full h-32 object-cover"
                  loading="lazy"
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Post Stats */}
      <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
        <span>{post.likeCount} likes</span>
        <span>{post.commentCount} comments</span>
        <span>{post.shareCount} shares</span>
      </div>

      {/* Time */}
      <div className="text-xs text-gray-400 mb-3">
        {formatDistanceToNow(post.createdAt)} ago
      </div>

      {/* Post Actions */}
      <div className="flex items-center justify-around pt-3 border-t border-gray-100">
        <button
          onClick={() => onLike(post.id)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
            post.isLiked ? 'text-red-500' : 'text-gray-600 hover:bg-gray-50'
          }`}
        >
          <Heart className={`w-5 h-5 ${post.isLiked ? 'fill-current' : ''}`} />
          <span className="text-sm">Like</span>
        </button>
        <Link
          to={`/feed`}
          className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors text-gray-600"
        >
          <MessageCircle className="w-5 h-5" />
          <span className="text-sm">Comment</span>
        </Link>
        <button
          onClick={() => onCopyLink(post.id)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors text-gray-600"
        >
          {copiedPostId === post.id ? (
            <>
              <Check className="w-5 h-5 text-green-500" />
              <span className="text-sm text-green-600">Copied!</span>
            </>
          ) : (
            <>
              <Link2 className="w-5 h-5" />
              <span className="text-sm">Share</span>
            </>
          )}
        </button>
      </div>
    </GovCard>
  )
})

SavedPostCard.displayName = 'SavedPostCard'

// Reel Card Component - Memoized for performance
const SavedReelCard = React.memo(({
  reel,
  onUnsave
}: {
  reel: Reel
  onUnsave: (id: number) => void
}) => {
  return (
    <Link
      to={`/reels?id=${reel.id}`}
      className="relative aspect-[9/16] rounded-xl overflow-hidden bg-gray-900 group"
    >
      {/* Video Thumbnail */}
      {reel.thumbnailUrl ? (
        <img
          src={getMediaUrl(reel.thumbnailUrl)}
          alt={reel.caption || 'Reel'}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
          <Play className="w-12 h-12 text-white/50" />
        </div>
      )}

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent">
        <div className="absolute bottom-0 left-0 right-0 p-3">
          <p className="text-white text-sm font-medium line-clamp-2">
            {reel.caption || `@${reel.user.username}`}
          </p>
          <div className="flex items-center gap-3 mt-1 text-white/70 text-xs">
            <span className="flex items-center gap-1">
              <Heart className="w-3 h-3" />
              {reel.likeCount}
            </span>
            <span className="flex items-center gap-1">
              <MessageCircle className="w-3 h-3" />
              {reel.commentCount}
            </span>
          </div>
        </div>
      </div>

      {/* Unsave Button */}
      <button
        onClick={(e) => {
          e.preventDefault()
          onUnsave(reel.id)
        }}
        className="absolute top-2 right-2 p-1.5 bg-black/50 rounded-full hover:bg-black/70 transition-colors"
        title="Remove from saved"
      >
        <Bookmark className="w-4 h-4 text-yellow-400 fill-yellow-400" />
      </button>
    </Link>
  )
})

SavedReelCard.displayName = 'SavedReelCard'

// Tab Button Component
const TabButton = React.memo(({
  active,
  onClick,
  icon: Icon,
  label
}: {
  active: boolean
  onClick: () => void
  icon: React.ComponentType<{ className?: string }>
  label: string
}) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
      active
        ? 'bg-blue-600 text-white'
        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
    }`}
  >
    <Icon className="w-5 h-5" />
    {label}
  </button>
))

TabButton.displayName = 'TabButton'

// Main Component
const SavedPostsPage = () => {
  const [activeTab, setActiveTab] = useState<'posts' | 'reels'>('posts')
  const [posts, setPosts] = useState<Post[]>([])
  const [reels, setReels] = useState<Reel[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const [copiedPostId, setCopiedPostId] = useState<number | null>(null)

  useEffect(() => {
    if (activeTab === 'posts') {
      fetchSavedPosts()
    } else {
      fetchSavedReels()
    }
  }, [activeTab, page])

  const fetchSavedPosts = useCallback(async () => {
    try {
      setLoading(true)
      const response = await postService.getSavedPosts(page)
      if (response.success) {
        const newPosts = response.data.content
        if (page === 0) {
          setPosts(newPosts)
        } else {
          setPosts(prev => [...prev, ...newPosts])
        }
        setHasMore(!response.data.last)
      }
    } catch (error) {
      console.error('Error fetching saved posts:', error)
    } finally {
      setLoading(false)
    }
  }, [page])

  const fetchSavedReels = useCallback(async () => {
    try {
      setLoading(true)
      const response = await reelService.getSavedReels(page, 20)
      if (response.content) {
        if (page === 0) {
          setReels(response.content)
        } else {
          setReels(prev => [...prev, ...response.content])
        }
        setHasMore(!response.last)
      }
    } catch (error) {
      console.error('Error fetching saved reels:', error)
    } finally {
      setLoading(false)
    }
  }, [page])

  const handleUnsave = useCallback(async (postId: number) => {
    try {
      await postService.toggleSave(postId)
      setPosts(posts.filter(p => p.id !== postId))
    } catch (error) {
      console.error('Error unsaving post:', error)
    }
  }, [posts])

  const handleUnsaveReel = useCallback(async (reelId: number) => {
    try {
      await reelService.toggleBookmark(reelId)
      setReels(reels.filter(r => r.id !== reelId))
    } catch (error) {
      console.error('Error unsaving reel:', error)
    }
  }, [reels])

  const handleLike = useCallback(async (postId: number) => {
    try {
      await postService.toggleLike(postId)
      setPosts(posts.map(p =>
        p.id === postId
          ? { ...p, isLiked: !p.isLiked, likeCount: p.isLiked ? p.likeCount - 1 : p.likeCount + 1 }
          : p
      ))
    } catch (error) {
      console.error('Error liking post:', error)
    }
  }, [posts])

  const handleShare = useCallback(async (postId: number) => {
    try {
      await postService.sharePost(postId)
      setPosts(posts.map(p =>
        p.id === postId
          ? { ...p, shareCount: p.shareCount + 1 }
          : p
      ))
    } catch (error) {
      console.error('Error sharing post:', error)
    }
  }, [posts])

  const handleCopyLink = useCallback((postId: number) => {
    const link = `${window.location.origin}/post/${postId}`
    navigator.clipboard.writeText(link).then(() => {
      setCopiedPostId(postId)
      setTimeout(() => setCopiedPostId(null), 2000)
      handleShare(postId)
    })
  }, [handleShare])

  const handleTabChange = useCallback((tab: 'posts' | 'reels') => {
    setActiveTab(tab)
    setPage(0)
    setPosts([])
    setReels([])
  }, [])

  const handleLoadMore = useCallback(() => {
    setPage(page + 1)
  }, [page])

  const itemCount = activeTab === 'posts' ? posts.length : reels.length

  // Loading spinner
  if (loading && posts.length === 0 && reels.length === 0) {
    return (
      <GovLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </GovLayout>
    )
  }

  return (
    <GovLayout>
      <div className="space-y-4">
        {/* Page Title */}
        <GovPageTitle
          title="Saved Items"
          subtitle={`${itemCount} ${activeTab === 'posts' ? 'posts' : 'reels'} saved`}
          icon={Bookmark}
        />

        {/* Tabs */}
        <div className="flex items-center gap-2">
          <TabButton
            active={activeTab === 'posts'}
            onClick={() => handleTabChange('posts')}
            icon={FileText}
            label="Posts"
          />
          <TabButton
            active={activeTab === 'reels'}
            onClick={() => handleTabChange('reels')}
            icon={Video}
            label="Reels"
          />
        </div>

        {/* Content */}
        {activeTab === 'posts' ? (
          // Posts Tab
          posts.length === 0 ? (
            <GovEmptyState
              icon="📑"
              title="No Saved Posts"
              description="Posts you save will appear here for easy access later"
              action={{ label: 'Browse Feed', onClick: () => window.location.href = '/feed' }}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {posts.map((post) => (
                <SavedPostCard
                  key={post.id}
                  post={post}
                  onUnsave={handleUnsave}
                  onLike={handleLike}
                  onCopyLink={handleCopyLink}
                  copiedPostId={copiedPostId}
                />
              ))}
            </div>
          )
        ) : (
          // Reels Tab
          reels.length === 0 ? (
            <GovEmptyState
              icon="🎬"
              title="No Saved Reels"
              description="Reels you bookmark will appear here for easy access later"
              action={{ label: 'Browse Reels', onClick: () => window.location.href = '/reels' }}
            />
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {reels.map((reel) => (
                  <SavedReelCard
                    key={reel.id}
                    reel={reel}
                    onUnsave={handleUnsaveReel}
                  />
                ))}
              </div>
            </>
          )
        )}

        {/* Load More Button */}
        {hasMore && (posts.length > 0 || reels.length > 0) && (
          <div className="flex justify-center pt-4">
            <GovButton
              variant="secondary"
              onClick={handleLoadMore}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Load More'}
            </GovButton>
          </div>
        )}
      </div>
    </GovLayout>
  )
}

export default React.memo(SavedPostsPage)